import React, { useEffect, useMemo, useState } from 'react'
import { useTranslate } from 'react-admin'
import {
  Tab as TabMUI,
  Box,
  Typography,
  Divider,
  Accordion as MuiAccordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Grid,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions
} from '@mui/material'
import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import { styled } from '@mui/material/styles'
import AirplanemodeActiveIcon from '@mui/icons-material/AirplanemodeActive'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AvTimerIcon from '@mui/icons-material/AvTimer'
import { LinearScale, VerticalScale } from 'src/components/uiElements/iconCSS/IconCSS'
import { AirSegmentIcon } from 'src/components/pages/sales/reservation/logic/air/AirIcons'
import { ShowElement } from 'src/components/uiElements/CRUD/ShowElement'
import { turquoise, lightcyan, blue } from 'src/assets/colors'
import { useImage } from 'src/components/customHooks/useImage'
import { formatDate } from '../../../business-rule/helper/formatDate'
import { ReactComponent as SmallBaggage } from 'src/assets/baggage/smallBaggage.svg';
import { ReactComponent as MediumBaggage } from 'src/assets/baggage/mediumBaggage.svg';
import { ReactComponent as LargeBaggage } from 'src/assets/baggage/largeBaggage.svg';

const Accordion = styled((props) => <MuiAccordion {...props} />)(() => ({
  '&:before': {
    display: 'none',
  },
}))

export const GetLogoImageAirline = React.memo(({ fileName, style }) => {
  const extensions = useMemo(() => ['png', 'jpg', 'jpeg', 'gif'], [])
  const { loading, error, image } = useImage({ fileName, extensions })
  if (error) return null
  return <>{loading ? <Typography>loading</Typography> : <img style={style} src={image} alt={fileName} />}</>
})

export const getAirports = (list) => {
  if (list && Array.isArray(list) && list.length !== 0) {
    let listAirports = list.map((data) => `${data.boardPoint}/${data.offPoint} - `)
    let concant = listAirports.join('')
    let delete_last_characters = concant?.slice(0, -2)
    return delete_last_characters
  }
}

export const AirScaleRow = ({ air }) => {
  const translate = useTranslate()
  const { itineraryGroup } = air.segments?.[0] || {}
  const { baggage } = air
  const [openConditionsDialog, setOpenConditionDialog] = useState(false)
  const [features, setFeatures] = useState()
  const [rules, setRules] = useState()

  const isParseable = (str) =>{
    try {
      const parsed = JSON.parse(str)
      return parsed
    } catch (e) {
      return null
    }
  }

  useEffect(()=>{
    setFeatures(air?.features ? (typeof air?.features === 'object' ? air?.features : isParseable(air?.features)) : null )
    setRules(air?.rule_data ? isParseable(air?.rule_data) : null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  const skipBaggage = useMemo(() => {
    let parameters = air?.product?.parameters ?? air?.product_relation?.parameters

    if (typeof parameters === 'string') {
      parameters = isParseable(parameters) || {}
    }
    return Boolean(
      parameters?.skip_baggage === true
    )
  }, [air])

const baggagePerLeg = useMemo(() => {
  if (skipBaggage || !baggage || !itineraryGroup) return []
  const arr = []
  let offset = 0
  itineraryGroup.forEach((leg) => {
    arr.push(baggage[offset] || {})
    offset += leg.scales?.length || 0
  })
  return arr
}, [skipBaggage, baggage, itineraryGroup])

  const howManyScales = (amount_scale) => {
    if (amount_scale === 0)
      return (
        <>
          <Typography
            component='div'
            style={{
              color: turquoise,
              fontWeight: 600,
              marginBottom: '5px',
            }}
          >
            {translate('ra.title.direct')}
          </Typography>
          <LinearScale />
        </>
      )
    if (amount_scale !== 0)
      return (
        <>
          <Typography
            component='div'
            style={{
              color: turquoise,
              fontWeight: 600,
              marginBottom: '5px',
            }}
          >
            {amount_scale} {translate('ra.title.scale')}
          </Typography>
          <LinearScale direct classToUse={amount_scale} />
        </>
      )
  }

  const getBaggages = (index, vertical = false) => {
    if (skipBaggage) return null
    const baggages = {
      small: {
        icon: <SmallBaggage />,
        title: translate('ra.baggage_type.small_baggage_title'),
        description: translate('ra.baggage_type.small_baggage_description')
      },
      medium: {
        icon: <MediumBaggage className='not-included' />,
        title: translate('ra.baggage_type.medium_baggage_not_included')
      },
      large: {
        icon: <LargeBaggage className='not-included' />,
        title: translate('ra.baggage_type.large_baggage_not_included')
      }
    }

    if (baggagePerLeg[index]?.CABIN_BAG && baggagePerLeg[index].CABIN_BAG.quantity > 0) {
      baggages['medium'] = {
        icon: <MediumBaggage />,
        title: translate('ra.baggage_type.medium_baggage_title'),
      description: baggagePerLeg[index].CABIN_BAG.description || translate('ra.baggage_type.medium_baggage_description')
      }
    }

    if (baggagePerLeg[index]?.CHECKED_BAG && baggagePerLeg[index].CHECKED_BAG.quantity > 0) {
      baggages['large'] = {
        icon: <LargeBaggage />,
        title: translate('ra.baggage_type.large_baggage_included'),
      description: baggagePerLeg[index].CHECKED_BAG.description || ''
      }
    }

    return <Grid container className='baggage-container'>
      {Object.values(baggages).map((elem, i) => (
        <Grid item xs={vertical ? 12 : 4} key={i}>
          <Grid container spacing={1} columns={24}>
            <Grid item xs={3} className='icon-container'>{elem.icon}</Grid>
            <Grid item xs={21} className='baggage-description'>
              <b>{elem.title}</b>
              {elem.description && <span>{elem.description}</span>}
            </Grid>
          </Grid>
        </Grid>
      ))}
    </Grid>
  }

  const getBaggageIcons = (index) => {
    if (skipBaggage) return null
    const baggages = {}

    const mediumBaggageNotIncluded = !baggagePerLeg[index]?.CABIN_BAG || baggagePerLeg[index].CABIN_BAG.quantity === 0
    if (mediumBaggageNotIncluded) {
      baggages['medium'] = 'not-included'
    }

    const largeBaggageNotIncluded = !baggagePerLeg[index]?.CHECKED_BAG || baggagePerLeg[index].CHECKED_BAG.quantity === 0
    if (largeBaggageNotIncluded) {
      baggages['large'] = 'not-included'
    }

    return <Tooltip title={getBaggages(index, true)}>
      <div>
        <SmallBaggage className='small' />
        <MediumBaggage className={`medium ${baggages['medium']}`} />
        <LargeBaggage className={`large ${baggages['large']}`} />
      </div>
    </Tooltip>
  }

  const getAncillariesByLeg = useMemo(() => {
    if (!air?.pax_relation || !itineraryGroup) return []
    
    const ancillariesByLeg = []
    
    const allAncillaries = []
    air.pax_relation.forEach((passenger) => {
      if (passenger.pax_item_relation) {
        passenger.pax_item_relation.forEach((paxItem) => {
          if (paxItem.baggage && Array.isArray(paxItem.baggage)) {
            paxItem.baggage.forEach((baggageItem) => {
              allAncillaries.push({
                passengerName: `${passenger.name} ${passenger.surname}`,
                offerName: baggageItem.offerName,
                offerIcon: baggageItem.offerIcon,
                passengerNumber: baggageItem.passengerNumber,
                segmentNumbers: baggageItem.segmentNumbers
              })
            })
          }
        })
      }
    })
    
    itineraryGroup.forEach((leg, legIndex) => {
      const legAncillaries = []
      
      let segmentOffset = 0
      for (let i = 0; i < legIndex; i++) {
        segmentOffset += itineraryGroup[i].scales?.length || 1
      }
      
      const currentLegSegments = []
      const scalesCount = leg.scales?.length || 1
      for (let i = 0; i < scalesCount; i++) {
        currentLegSegments.push(segmentOffset + i + 1)
      }
      
      allAncillaries.forEach((ancillary) => {
        if (ancillary.segmentNumbers && ancillary.segmentNumbers.length > 0) {
          const belongsToLeg = ancillary.segmentNumbers.some(segmentNum => 
            currentLegSegments.includes(segmentNum)
          )
          
          if (belongsToLeg) {
            legAncillaries.push({
              passengerName: ancillary.passengerName,
              offerName: ancillary.offerName,
              offerIcon: ancillary.offerIcon,
              passengerNumber: ancillary.passengerNumber
            })
          }
        }
      })
      
      ancillariesByLeg.push(legAncillaries)
    })
    
    return ancillariesByLeg
  }, [air?.pax_relation, itineraryGroup, air?.id])

  const renderAncillaries = (legIndex) => {
    const legAncillaries = getAncillariesByLeg[legIndex] || []
    
    if (legAncillaries.length === 0) return null
    
    const getBaggageIcon = (iconType) => {
      switch (iconType) {
        case 'large-baggage':
          return <LargeBaggage style={{ color: '#7cbe4c' }} />
        case 'medium-baggage':
          return <MediumBaggage style={{ color: '#7cbe4c' }} />
        case 'small-baggage':
          return <SmallBaggage style={{ color: '#7cbe4c' }} />
        default:
          return <LargeBaggage style={{ color: '#7cbe4c' }} />
      }
    }
    
    return (
      <Box width='90%' marginLeft='auto' paddingY='20px'>
        <Typography
          component='div'
          style={{
            color: turquoise,
            fontWeight: 600,
            marginBottom: '15px',
          }}
        >
          {translate('ra.title.extra_baggage')}
        </Typography>
        <Grid container spacing={2}>
          {legAncillaries.map((ancillary, index) => (
            <Grid item xs={12} key={index}>
              <Box display='flex' alignItems='flex-start' gap={2}>
                <Box className='icon-container'>
                  {getBaggageIcon(ancillary.offerIcon)}
                </Box>
                <Box display='flex' flexDirection='column'>
                  <Typography component='div' fontWeight={600} style={{ marginBottom: '4px' }}>
                    {translate('ra.field.passenger')} {ancillary.passengerName}
                  </Typography>
                  <Typography component='div' fontWeight={400} color='textSecondary'>
                    {ancillary.offerName}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    )
  }

  return (
    <Box style={{ width: '100%', height: 'auto', margin: '15px 0 15px 0' }}>
      <Typography variant='h6' component='span' color={blue} padding={'0px 5px 0px 5px'}>
        {translate('ra.title.segments')} ({itineraryGroup?.length})
      </Typography>

      <TabContext value={'1'}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TabList aria-label='lab API tabs example'>
            <TabMUI label={getAirports(itineraryGroup)} value='1' />
          </TabList>
        </Box>

        <TabPanel value='1'>
          {itineraryGroup?.map((flightInformation, index) => {
            return (
              <Accordion
                key={index}
                style={{
                  boxShadow: 'none',
                  backgroundColor: lightcyan,
                  borderRadius: '10px',
                  margin: '10px 0 10px 0',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls='panel1a-content'
                  id='panel1a-header'
                >
                  <Box className='accordion_summary_container'>
                    {itineraryGroup?.length <= 2 && index === 0 ? (
                      <AirSegmentIcon
                        style={{ transform: 'rotate(90deg)' }}
                        icon={<AirplanemodeActiveIcon />}
                        text={translate('ra.title.going')}
                      />
                    ) : itineraryGroup?.length <= 2 && index === 1 ? (
                      <AirSegmentIcon
                        style={{ transform: 'rotate(-90deg)', margin: '0 0 0 7px' }}
                        icon={<AirplanemodeActiveIcon />}
                        text={translate('ra.title.return')}
                      />
                    ) : undefined}
                    <Box display={'flex'}>
                      <Box className='accordion_summary_column' position={'relative'}>
                        <Typography
                          variant='h6'
                          component='div'
                          className='accordion_summary_column_absolute'
                        >
                          {translate('ra.title.segment')} {index + 1}
                        </Typography>
                        <Box
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                          }}
                        >
                          <GetLogoImageAirline
                            fileName={flightInformation?.airline || undefined}
                            style={{ height: '25px', width: 'auto' }}
                          />
                        </Box>
                      </Box>
                      <Box className='accordion_summary_column'>
                        <Typography component='div'>{translate('ra.title.departure')}</Typography>
                        <Typography
                          variant='h7'
                          component='div'
                          style={{
                            fontWeight: 600,
                          }}
                        >
                          {flightInformation.boardPoint}
                        </Typography>
                        <Typography
                          component='div'
                          style={{
                            color: blue,
                          }}
                        >
                          {formatDate(flightInformation.departureDate)}
                        </Typography>
                        <Typography
                          component='div'
                          style={{
                            color: blue,
                          }}
                        >
                          {flightInformation.departureTime?.slice(0, 5) || 'aaaaaaaaaa'}
                        </Typography>
                      </Box>
                      <Box className='accordion_summary_column'>
                        {howManyScales(flightInformation?.scales?.length - 1)}
                      </Box>
                      <Box className='accordion_summary_column'>
                        <Typography component='div'>{translate('ra.title.arrival')}</Typography>
                        <Typography
                          component='div'
                          style={{
                            fontWeight: 600,
                          }}
                        >
                          {flightInformation.offPoint}
                        </Typography>
                        <Typography
                          variant='h7'
                          component='div'
                          style={{
                            color: blue,
                          }}
                        >
                          {formatDate(flightInformation.arrivalDate)}
                        </Typography>
                        <Typography
                          variant='h7'
                          component='div'
                          style={{
                            color: blue,
                          }}
                        >
                          {flightInformation.arrivalTime?.slice(0, 5)}
                        </Typography>
                      </Box>
                      <Box className='accordion_summary_column'>
                        <Typography
                          component='div'
                          style={{
                            fontWeight: 600,
                          }}
                        >
                          {translate('ra.title.duration')}
                        </Typography>

                        <Typography component='div' style={{ textAlign: 'center' }}>
                          {flightInformation.totalDuration ?? flightInformation.totalDuration}
                        </Typography>
                      </Box>
                      {!skipBaggage && (
                        <Box className='accordion_summary_column'>
                          <Box height={'30px'} width={'auto'} className='baggage-container-icons'>
                            {getBaggageIcons(index)}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {flightInformation?.scales && flightInformation.scales.length >= 1 ?
                    flightInformation.scales.map((scale, scaleIndex) => {
                      return (
                        <Box margin={'10px 0 10px 0'} key={scaleIndex}>
                          {scaleIndex !== 0 ? (
                            <Box className='accordion_details_scale'>
                              <Typography component='div' className='number_scale'>
                                {translate('ra.title.scale')} {scaleIndex}
                              </Typography>
                              <Box className='wait_info_scale' display='flex'>
                                <AvTimerIcon style={{ marginRight: 10 }} />
                                <Typography component='div' color={turquoise} fontWeight={600}>
                                  {translate('ra.title.wait_in')} {scale.boardPointName} (
                                  {scale.boardPointCityCode}){scale.waitTime && ` - ${scale.waitTime}`}
                                </Typography>
                              </Box>
                            </Box>
                          ) : undefined}
                          <Box className='accordion_details_scale_detail'>
                            <Box className='accordion_details_scale_detail_container'>
                              <Box className='accordion_details_scale_detail_segment_icon'>
                                <VerticalScale />
                              </Box>
                              <Box className='accordion_details_scale_detail_segment'>
                                <Box className='airport_detail'>
                                  <Typography component='span' color={blue} fontWeight={600}>
                                    {scale.boardPoint}
                                  </Typography>
                                  <Typography component='span' color={turquoise} fontWeight={600}>
                                    {formatDate(scale.departureDate)}
                                  </Typography>
                                  <Typography component='span' color={turquoise} fontWeight={600}>
                                    {scale.departureTime}
                                  </Typography>
                                  <Typography
                                    component='div'
                                    fontWeight={600}
                                    width={'100%'}
                                    textAlign={'center'}
                                  >
                                    {scale.boardPointName}
                                  </Typography>
                                </Box>
                                <Box className='airport_detail'>
                                  <Typography component='span' color={blue} fontWeight={600}>
                                    {scale.offPoint}
                                  </Typography>
                                  <Typography component='span' color={turquoise} fontWeight={600}>
                                    {formatDate(scale.arrivalDate)}
                                  </Typography>
                                  <Typography component='span' color={turquoise} fontWeight={600}>
                                    {scale.arrivalTime}
                                  </Typography>
                                  <Typography
                                    fontWeight={600}
                                    component='div'
                                    width={'100%'}
                                    textAlign={'center'}
                                  >
                                    {scale.offPointName}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box
                                className='accordion_details_scale_detail_segment'
                                display={'flex'}
                                padding={'0 5px 0 5px'}
                              >
                                <Box className='flight_detail'>
                                  <ShowElement
                                    data={scale.operatingCompany}
                                    label={translate('ra.field.operator_company')}
                                  />
                                  <ShowElement
                                    data={scale.bookingClass}
                                    label={translate('ra.field.booking_class')}
                                  />
                                  <ShowElement
                                    data={scale.fareBasis}
                                    label={translate('ra.field.fare_basis')}
                                  />
                                  <ShowElement
                                    data={scale.flightNumber}
                                    label={translate('ra.field.flight')}
                                  />
                                </Box>
                                <Box className='flight_detail'>
                                  <ShowElement
                                    data={scale.marketingCompany}
                                    label={translate('ra.field.airline')}
                                  />
                                  <ShowElement
                                    data={scale.equipmentType}
                                    label={translate('ra.field.equipmentType')}
                                  />
                                  <ShowElement data={scale.Cabin} label={translate('ra.field.Cabin')} />
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      )
                    }) : undefined
                  }
                  {baggage && !skipBaggage && (
                    <Box width='90%' marginLeft='auto' paddingY='20px'>
                      {getBaggages(index)}
                    </Box>
                  )}
                  
                  {/* Nueva sección de equipajes extra */}
                  {renderAncillaries(index)}
                </AccordionDetails>
              </Accordion>
            )
          })}
        </TabPanel>
      </TabContext>
      {(features || rules) ? <Button style={{ fontWeight: 600, textDecoration: 'underline', marginBottom:'5px' }}  onClick={()=>{setOpenConditionDialog(true)}}>
          {translate('ra.action.see_conditions')}
      </Button> : ''}
      <Dialog open={openConditionsDialog} maxWidth='lg' fullWidth={true} onClose={()=>setOpenConditionDialog(false)}>
          <DialogTitle>{translate('ra.title.condition')}</DialogTitle>
            <DialogContent >
              <Grid container spacing={5} >
                  {features && (<Grid item xs={12} md={12} lg={12} xl={12}>
                    <Typography 
                        component='div'
                        fontWeight={600}
                        width={'100%'}
                        textAlign={'start'}
                        fontSize={'25px'}
                        color={'primary'}
                      >
                        {translate('ra.title.general_conditions')}
                    </Typography>
                    <Box>
                      
                      {features.SEAT_SELECTION && (<ShowElement
                        data={features.SEAT_SELECTION.description}
                        label={translate('ra.field.seat_selection')}
                      />)}
                      {features.MEAL && (<ShowElement
                        data={features.MEAL.description}
                        label={translate('ra.field.food')}
                      />)}
                      {features.CHANGEABLE && (<ShowElement
                        data={features.CHANGEABLE.description}
                        label={translate('ra.field.changes')}
                      />)}
                      {features.REFUNDABLE && (<ShowElement
                        data={features.REFUNDABLE.description}
                        label={translate('ra.field.refoundable')}
                      />)}
                    </Box>
                  </Grid>)}
                  {rules && (<Grid item xs={12} md={12} lg={12} xl={12}>
                  <Typography 
                        component='div'
                        fontWeight={600}
                        width={'100%'}
                        textAlign={'start'}
                        fontSize={'25px'}
                        color={'primary'}
                      >
                        {translate('ra.title.fee_conditions')}
                    </Typography>
                    <Box>
                      {rules?.map(rule=>(
                        <>
                          <ShowElement
                            label={rule}
                          />
                        </>
                      ))}
                      
                    </Box>
                  </Grid>)}
              </Grid>
              <DialogActions className='dialog-actions' style={{ marginTop: '20px' }}>
                <Button onClick={()=>setOpenConditionDialog(false)} variant='outlined' color='error'>
                  {translate('ra.action.close')}
                </Button>
              </DialogActions>
            </DialogContent>
          </Dialog>
      <Divider />
    </Box>
  )
}
